
/*******************************************************************************************
   _____            __              ____   ____                         .__               
  /  _  \   _______/  |________  ___\   \ /   /____   ____  __ __  _____|__|____    ____  
 /  /_\  \ /  ___/\   __\_  __ \/  _ \   Y   // __ \ /    \|  |  \/  ___/  \__  \  /    \ 
/    |    \\___ \  |  |  |  | \(  <_> )     /\  ___/|   |  \  |  /\___ \|  |/ __ \|   |  \
\____|__  /____  > |__|  |__|   \____/ \___/  \___  >___|  /____//____  >__(____  /___|  /
        \/     \/                                 \/     \/           \/        \/     \/ 
********************************************************************************************
Auth Context
********************************************************************************************

Author:     Nicholas Hamilton, PhD
Email:      nicholasehamilton@gmail.com
Date:       23rd April 2025

*******************************************************************************************/
import React from 'react';

const DEFAULT_TIMEOUT_SECONDS = 5;

/**
 * React hook to manage a temporary lock state.
 * Useful for preventing duplicate actions, race conditions, or enforcing cooldowns.
 *
 * @param {Object} [options] - Configuration options for the lock.
 * @param {string} [options.name="lock"] - Optional identifier for the lock (useful for debugging).
 * @param {number} [options.timeout=DEFAULT_TIMEOUT_SECONDS] - Time in seconds before the lock auto-releases.
 * @returns {{
*   locked: boolean,
*   acquire: () => void,
*   release: () => void
* }} An object containing:
*   - `locked`: Whether the lock is currently held.
*   - `acquire()`: Function to activate the lock.
*   - `release()`: Function to manually clear the lock before timeout.
*/
export const useLock = ({name = "lock", timeout = DEFAULT_TIMEOUT_SECONDS} = {}) => {

    const [locked,setLocked]    = React.useState(false);
    const lockedRef             = React.useRef(locked);

    React.useEffect(() => {
        lockedRef.current = locked;
    },[locked])
    
    const acquire = React.useCallback(() => new Promise((resolve, reject) => {
        const startTime = Date.now();
        const timeoutMs = Math.max(timeout * 1000, 0);
        const checkLock = () => {
            if (lockedRef.current) {
                if (Date.now() - startTime > timeoutMs) {
                    reject(new Error(`Timeout: ${name} could not acquire lock within ${timeout} seconds`));
                } else {
                    setTimeout(checkLock, 100);
                }
            } else {
                setLocked(true);
                resolve(true); // Lock acquired successfully
            }
        };
        checkLock(); // Start checking the lock
    }), [timeout, name]);

    const release = React.useCallback( () => {
        setLocked(false);
    }, []);

    // Unlock on Unmount
    React.useEffect(() => {
        return () => {
            setLocked(false);
        }
    },[])

    return {
        locked,
        acquire,
        release,
    }
}

export default useLock;